// react core
import { useContext, useState } from "react";

// material design
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

// fonts
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenLine as iconTagline } from "@fortawesome/pro-duotone-svg-icons";
import { faInfoCircle as iconHappening } from "@fortawesome/pro-duotone-svg-icons";
import { faClose as iconClose } from "@fortawesome/pro-thin-svg-icons";
import { faTimes as iconInactive } from "@fortawesome/pro-duotone-svg-icons";
import { faEdit as iconEdit } from "@fortawesome/pro-duotone-svg-icons";
import { faCircle as iconCircle } from "@fortawesome/pro-solid-svg-icons";

// entzy config and components
import configEntzy from "components/config/ConfigEntzy";
// import { MainContext } from "components/main/MainContext";
import { EventContext } from "components/event/EventContext";
import InputForm from "components/input/InputForm";
import {
  ActionButton,
  ActionBoxButton,
} from "components/utils/common/CommonButtons";
import {
  ActionCheck,
  ActionLoader,
} from "components/utils/common/CommonLoaders";
import { BlockRichText } from "components/utils/common/CommonRichText";
import { HeaderTag } from "components/utils/common/CommonTags";
import { createEventStructureConstants } from "models/event";
import { scrollToId } from "services/tools";

function ActionLaunchpadRunnerDetails(props) {
  const section = "details";
  // const user = props.user;
  const editing = props.editing;

  const handleEdit = props.handleEdit;
  const handleRunnerActionCallback = props.handleRunnerActionCallback;

  const anchorTop = "anchor-launchpad-edit-" + section + "-top";

  // const mainContext = useContext(MainContext);
  const eventContext = useContext(EventContext);

  const eventConstants = createEventStructureConstants();

  // establish selected category as a constant based on current event details
  let selectedMeetCategory = null;
  if (eventContext.state.event.data.Details) {
    selectedMeetCategory = "custom";
    eventConstants.meetCategories.forEach((category) => {
      if (
        eventContext.state.event.data.Details.includes(category.title) &&
        eventContext.state.event.data.Tagline === category.tagline
      ) {
        selectedMeetCategory = category.id;
      }
    });
  }

  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({
    visible: false,
  });
  // const [customDetails, setCustomDetails] = useState(
  //   selectedMeetCategory === "custom" ? true : false
  // );
  const [customDetails, setCustomDetails] = useState(true);

  const handleConfirmSelectMeetCategory = async (event, confirm, category) => {
    if (confirm === true) {
      setDialog({ visible: false });
      handleSelectMeetCategory(category);
    } else if (confirm === false) {
      setDialog({ visible: false });
    } else {
      setDialog({
        visible: true,
        title: "Setting Launch Details",
        description:
          "Confirm ok to set the launch details to category " +
          category.id.toUpperCase() +
          "? This will immediately replace any existing text.",
        cancel: (e) => handleConfirmSelectMeetCategory(e, false, category),
        confirm: (e) => handleConfirmSelectMeetCategory(e, true, category),
      });
    }
  };

  const handleSelectMeetCategory = async (category) => {
    if (!category) {
      setCustomDetails(false);
      return;
    }
    scrollToId(anchorTop, "instant");
    if (category.id === "custom") {
      setCustomDetails(true);
    } else if (category.id === "clear") {
      setCustomDetails(false);
      handleRunnerActionCallback([
        { id: "event-tagline", value: "" },
        { id: "event-details", value: "" },
      ]);
    } else {
      setCustomDetails(false);
      let response, contentData, updateData;
      response = await eventContext.getEventContentDataFromCategory(category);
      if (response.alert) {
        setLoading(false);
        eventContext.updateAlert(response);
        return response;
      }
      contentData = response.data;
      updateData = [];
      updateData.push({
        id: "event-tagline",
        value: contentData.Tagline,
      });
      updateData.push({
        id: "event-details",
        value: contentData.Details,
      });
      setLoading(true);
      await handleRunnerActionCallback(updateData);
      setLoading(false);
      handleEdit(null);
    }
  };

  return (
    <Box id={anchorTop} className="box-default">
      <Box className="box-default">
        <Typography variant="h6" color="primary">
          Launch Details
        </Typography>
        <Typography variant="subtitle1" color="white">
          <em>Set what happens on launch</em>
        </Typography>
      </Box>
      {editing ? (
        <Box
          className="box-default"
          sx={{
            pt: configEntzy.APP_SPACING_LG,
          }}
        >
          {loading && (
            <Box className="box-default zero-height">
              <Box
                className="box-inline bg-black-t90 relative pop-up"
                sx={{
                  mt: configEntzy.APP_SPACING_HL,
                  p: configEntzy.APP_SPACING_MD,
                  borderRadius: "50%",
                }}
              >
                <ActionLoader />
              </Box>
            </Box>
          )}
          <Box className="box-default" hidden={customDetails}>
            <Box
              className="box-default"
              sx={{
                p: configEntzy.APP_SPACING_MD,
              }}
            >
              <Divider>
                <Typography variant="subtitle2" color="primary">
                  Auto Populate from Category
                </Typography>
              </Divider>
              <Typography variant="subtitle2" color="secondary">
                Select a repeatable meet
              </Typography>
            </Box>
            <Box
              className="box-default"
              sx={{
                p: configEntzy.APP_SPACING_MD,
              }}
            >
              {eventConstants.meetCategories.map((category) => {
                return (
                  <Box
                    key={category.id}
                    className="box-default half-width"
                    sx={{ p: configEntzy.APP_SPACING_XS }}
                  >
                    <ActionBoxButton
                      size="small"
                      bgColor={
                        selectedMeetCategory === category.id
                          ? "primary.main"
                          : "black"
                      }
                      color={
                        selectedMeetCategory === category.id ? "black" : "white"
                      }
                      text={category.id}
                      subText={category.title}
                      subTextNoWrap={true}
                      onClick={() => {
                        handleConfirmSelectMeetCategory(null, null, category);
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
            <Box
              className="box-default"
              sx={{
                mt: configEntzy.APP_SPACING_LG,
                p: configEntzy.APP_SPACING_MD,
              }}
            >
              <Divider>
                <Typography variant="subtitle2" color="primary">
                  Or
                </Typography>
              </Divider>
            </Box>
            <Box
              className="box-default"
              sx={{
                p: configEntzy.APP_SPACING_MD,
              }}
            >
              <ActionBoxButton
                size="small"
                bgColor={
                  selectedMeetCategory === "custom" ? "primary.main" : "black"
                }
                color={selectedMeetCategory === "custom" ? "black" : "white"}
                text="Custom Details"
                subText="Set your own content"
                onClick={() => {
                  handleSelectMeetCategory({ id: "custom" });
                }}
              />
            </Box>
            <Box
              className="box-default"
              sx={{
                mt: configEntzy.APP_SPACING_LG,
                p: configEntzy.APP_SPACING_MD,
              }}
            >
              <Divider>
                <Typography variant="subtitle2" color="primary">
                  Cancel
                </Typography>
              </Divider>
            </Box>
            <Box
              className="box-default"
              sx={{
                p: configEntzy.APP_SPACING_MD,
              }}
            >
              <ActionButton
                size="small"
                variant="outlined"
                color="bright"
                text="Back to Custom Details"
                onClick={() => {
                  // handleEdit(null);
                  handleSelectMeetCategory({ id: "custom" });
                }}
              />
            </Box>
          </Box>
          <Box className="box-default" hidden={!customDetails}>
            <Box className="box-default">
              <HeaderTag text={"Eventuator Tagline and Details"} />
            </Box>
            <Box className="box-default">
              <InputForm
                navigate={props.navigate}
                fields={[
                  {
                    id: "event-tagline",
                    type: "text-standard",
                    // label: "Eventuator Tagline",
                    placeholder: "Eventuator Tagline",
                    value: eventContext.state.event.data.Tagline
                      ? eventContext.state.event.data.Tagline
                      : "",
                    maxLength: eventConstants.maxTaglineLength,
                    disablePadding: true,
                    dark: true,
                  },
                  {
                    id: "event-details",
                    type: "text-rich",
                    // label: "What happens on Launch",
                    placeholder: "What happens on Launch",
                    value: eventContext.state.event.data.Details
                      ? eventContext.state.event.data.Details
                      : "",
                    maxLength: eventConstants.maxDetailsLength,
                    required: true,
                    disablePadding: true,
                  },
                ]}
                // warnOnChange={true} // (no longer needed as warnings are now in edit changes)
                disablePadding={true}
                submitText="Update Launch Details"
                callback={handleRunnerActionCallback}
                // cancel={() => handleSelectMeetCategory(null)}
                cancel={() => {
                  handleEdit(null);
                }}
                cancelText={
                  <span>
                    <span>Exit Details Editor</span>
                    <span>&nbsp;&nbsp;</span>
                    <FontAwesomeIcon
                      icon={iconClose}
                      transform="grow-6 right-3"
                    />
                  </span>
                }
                cancelTitle="Double checking..."
                cancelDescription="Are you sure you want to exit the custom editor without saving? If you have any new edits be sure to save first."
              />
            </Box>
          </Box>
          <Box className="box-default" hidden={!customDetails}>
            <Box
              className="box-default text-paragraph"
              sx={{
                mt: configEntzy.APP_SPACING_MD2X,
                p: configEntzy.APP_SPACING_MD,
              }}
            >
              <Divider>
                <Typography variant="subtitle2" color="primary">
                  Auto Populate Options
                </Typography>
              </Divider>
              <Typography
                variant="subtitle2"
                color="secondary"
                sx={{
                  mt: configEntzy.APP_SPACING_MD2X,
                }}
              >
                Need some inspiration?
              </Typography>
              <Typography variant="subtitle2" color="white">
                Choose from a set of repeatable meet categories
              </Typography>
            </Box>
            <Box
              className="box-default"
              sx={{
                p: configEntzy.APP_SPACING_MD,
              }}
            >
              <ActionButton
                size="small"
                variant="outlined"
                color="bright"
                text="Show Categories"
                onClick={() => {
                  setCustomDetails(false);
                }}
              />
            </Box>
          </Box>
          <Box className="box-default">
            <ActionCheck
              dialog={dialog.visible}
              title={dialog.title}
              description={dialog.description}
              cancel={dialog.cancel}
              confirm={dialog.confirm}
            />
          </Box>
        </Box>
      ) : (
        <Box
          className="box-default action-pointer"
          sx={{
            mt: configEntzy.APP_SPACING_LG,
          }}
          onClick={() => {
            handleEdit("details");
            handleSelectMeetCategory({ id: "custom" });
          }}
        >
          <Box
            className="box-default bg-black-t50 shadow-dark"
            sx={{
              p: configEntzy.APP_SPACING_MD,
              borderRadius: configEntzy.BORDER_SIZE_XL,
              color: "white",
            }}
          >
            <Box
              className="box-default text-left"
              sx={{
                pb: configEntzy.APP_SPACING_MD,
                borderBottom: "1px solid #ccc",
              }}
            >
              <Typography variant="subtitle2">
                <FontAwesomeIcon icon={iconTagline} />
                <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
                <span>Eventuator Tagline</span>
              </Typography>
            </Box>
            <Box className="box-default text-left">
              <Typography
                variant="subtitle2"
                sx={{
                  mt: configEntzy.APP_SPACING_MD,
                }}
              >
                <em>
                  {eventContext.state.event.data.Tagline ? (
                    eventContext.state.event.data.Tagline
                  ) : (
                    <span>
                      <span>Tagline Inactive&nbsp;&nbsp;&nbsp;&nbsp;</span>
                      <FontAwesomeIcon icon={iconInactive} color="red" />
                    </span>
                  )}
                </em>
              </Typography>
            </Box>
          </Box>
          {/* EDIT ICON IN MIDDLE */}
          {/* ----------------------------------------- */}
          <Box className="box-default zero-height text-right">
            <Typography variant="h1" color="primary">
              <span className="fa-layers fa-fw">
                <FontAwesomeIcon
                  icon={iconCircle}
                  color="#ccc"
                  transform="grow-15 up-8"
                />
                <FontAwesomeIcon
                  icon={iconCircle}
                  color="white"
                  transform="grow-14 up-8"
                />
                <FontAwesomeIcon icon={iconEdit} transform="up-8" />
              </span>
            </Typography>
          </Box>
          {/* ----------------------------------------- */}
          <Box
            className="box-default bg-black-t50 shadow-dark"
            sx={{
              mt: configEntzy.APP_SPACING_MD,
              p: configEntzy.APP_SPACING_MD,
              borderRadius: configEntzy.BORDER_SIZE_XL,
              color: "white",
            }}
          >
            <Box
              className="box-default text-left"
              sx={{
                pb: configEntzy.APP_SPACING_MD,
                borderBottom: "1px solid #ccc",
              }}
            >
              <Typography variant="subtitle2">
                <FontAwesomeIcon icon={iconHappening} />
                <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
                <span>What happens on Launch</span>
              </Typography>
            </Box>
            {eventContext.state.event.data.Details ? (
              <Box
                className="box-default text-paragraph text-center"
                sx={{
                  pt: configEntzy.APP_SPACING_MD2X,
                  pb: configEntzy.APP_SPACING_MD,
                }}
              >
                <Container maxWidth="xs">
                  <BlockRichText
                    content={eventContext.state.event.data.Details}
                    moderation={eventContext.state.event.data.ContentModeration}
                  />
                </Container>
              </Box>
            ) : (
              <Box
                className="box-default"
                sx={{
                  pt: configEntzy.APP_SPACING_LG,
                  pb: configEntzy.APP_SPACING_LG,
                }}
              >
                <Typography variant="subtitle2">
                  <span>No details set</span>
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default ActionLaunchpadRunnerDetails;
